import * as coachEngine from '@lift2fit/coach-engine';
import rawPacket from '@/data/coach-input.json';

/**
 * The Coach preview on the landing page is not marketing copy pasted into HTML:
 * it runs the real evaluation matrix over the real 4-week evidence packet that
 * `public.coach_gather_window()` produced from the seeded database
 * (12 sessions · 139 logged sets · 10 personal records).
 *
 * The engine's outputs are normalised defensively so a shape change can never
 * take the marketing page down — worst case the model degrades, it never throws.
 */

export interface CoachMetric {
  label: string;
  value: string;
  hint?: string;
}

export interface CoachChange {
  exercise: string;
  change: string;
  /** Machine action from the engine: increase_load, hold_add_rep, deload, blocked… */
  action: string;
  rule: string;
  rationale: string;
}

export interface CoachPreviewModel {
  verdict: string;
  verdictLabel: string;
  headline: string;
  metrics: CoachMetric[];
  flags: string[];
  changes: CoachChange[];
  railsHit: string[];
  markdown: string;
  sessionsAnalyzed: number;
  prCount: number;
  source: 'engine' | 'fallback';
}

type Bag = Record<string, unknown>;

const bag = (value: unknown): Bag => (value && typeof value === 'object' ? (value as Bag) : {});
const num = (value: unknown): number | null => (typeof value === 'number' && Number.isFinite(value) ? value : null);
const str = (value: unknown): string => (typeof value === 'string' ? value : '');
const arr = (value: unknown): unknown[] => (Array.isArray(value) ? value : []);

const VERDICT_LABEL: Record<string, string> = {
  on_track: 'On track',
  progressing_fast: 'Progressing fast',
  stalled: 'Stalled',
  overreaching: 'Overreaching',
  deload_recommended: 'Deload recommended',
  insufficient_data: 'Not enough data yet',
};

function kg(value: number | null): string {
  return value === null ? '—' : `${Math.round(value * 10) / 10} kg`;
}

function formatField(field: string, value: unknown): string {
  const numeric = num(value);
  if (numeric === null) return str(value) || '—';
  if (field === 'targetWeightKg') return kg(numeric);
  if (field === 'targetRpe') return `RPE ${numeric}`;
  return String(numeric);
}

const FIELD_LABELS: Record<string, string> = {
  sets: 'sets',
  repMin: 'rep range',
  repMax: 'rep range',
  targetWeightKg: 'load',
  targetRpe: 'target RPE',
};

const CHANGE_FIELDS = ['sets', 'targetWeightKg', 'targetRpe', 'repMin', 'repMax'] as const;

/** Plain-English fallback when a change carries no field delta (a hold, a deload, a block). */
const ACTION_LABEL: Record<string, string> = {
  increase_load: 'add load',
  hold_and_add_rep: 'hold load · add a rep',
  reduce_load: 'reduce load 5%',
  deload: 'deload: volume −40%',
  blocked: 'blocked — substitute',
  no_change: 'hold as prescribed',
};

/** Turn whatever shape the engine emits into one readable line per change. */
function describeChange(change: Bag): string {
  const before = bag(change.before);
  const after = bag(change.after);
  const hasNested = Object.keys(before).length > 0 || Object.keys(after).length > 0;

  const parts: string[] = [];
  for (const field of CHANGE_FIELDS) {
    const fromValue = hasNested ? before[field] : change[`from${field[0]?.toUpperCase()}${field.slice(1)}`];
    const toValue = hasNested ? after[field] : change[`to${field[0]?.toUpperCase()}${field.slice(1)}`];
    if (toValue === undefined) continue;
    if (field === 'repMin' && num(before.repMax) === num(after.repMax ?? toValue)) continue;
    const fromText = formatField(field, fromValue ?? before[field]);
    const toText = formatField(field, toValue);
    if (fromText === toText) continue;
    parts.push(`${FIELD_LABELS[field] ?? field} ${fromText} → ${toText}`);
  }

  if (parts.length === 0) {
    const explicit = str(change.change) || str(change.description);
    if (explicit) return explicit;
    return ACTION_LABEL[str(change.action)] ?? 'hold as prescribed';
  }
  return parts.join(' · ');
}

function toFlags(value: unknown): string[] {
  return arr(value).map((flag) => {
    if (typeof flag === 'string') return flag;
    const bagged = bag(flag);
    const label = str(bagged.label) || str(bagged.code) || str(bagged.type) || str(bagged.kind) || 'fatigue flag';
    const detail = str(bagged.detail) || str(bagged.explanation) || str(bagged.message);
    return detail ? `${label} — ${detail}` : label;
  });
}

export function buildCoachPreview(): CoachPreviewModel {
  const packet = rawPacket as unknown as Parameters<typeof coachEngine.evaluateBlock>[0];

  try {
    const evaluation = coachEngine.evaluateBlock(packet);
    const proposal = coachEngine.proposeNextWeek(packet, evaluation);
    const { proposal: safeProposal, railsHit } = coachEngine.applySafetyRails(proposal, evaluation, packet.profile);

    const evalBag = bag(evaluation);
    const metricsBag = bag(evalBag.metrics);
    const propBag = bag(safeProposal);

    const metrics: CoachMetric[] = [
      {
        label: 'Volume this week',
        value: kg(num(metricsBag.totalVolumeKg)),
        hint: num(metricsBag.volumeChangePct) === null ? undefined : `${num(metricsBag.volumeChangePct)}% vs last week`,
      },
      {
        label: 'Compliance',
        value: num(metricsBag.compliancePct) === null ? '—' : `${num(metricsBag.compliancePct)}%`,
        hint: `${num(metricsBag.sessionsAnalyzed) ?? 0} sessions analysed`,
      },
      {
        label: 'Average RPE',
        value: num(metricsBag.avgRpe) === null ? '—' : num(metricsBag.avgRpe)!.toFixed(1),
        hint: num(metricsBag.acuteChronicRatio) === null
          ? undefined
          : `acute:chronic ${num(metricsBag.acuteChronicRatio)!.toFixed(2)}`,
      },
      {
        label: 'PRs in window',
        value: String(num(metricsBag.prCount) ?? 0),
        hint: `${num(metricsBag.weeksAnalyzed) ?? 0} weeks of history`,
      },
    ];

    const verdict = str(evalBag.verdict) || 'on_track';

    return {
      verdict,
      verdictLabel: VERDICT_LABEL[verdict] ?? verdict.replace(/_/g, ' '),
      headline: str(evalBag.headline) || 'Your block is progressing as prescribed.',
      metrics,
      flags: toFlags(evalBag.fatigueFlags),
      changes: arr(propBag.changes).map((change) => {
        const bagged = bag(change);
        return {
          exercise: str(bagged.exerciseName) || str(bagged.exerciseId) || 'Exercise',
          change: describeChange(bagged),
          action: str(bagged.action) || 'no_change',
          rule: str(bagged.rule) || 'double_progression',
          rationale: str(bagged.rationale),
        };
      }),
      railsHit: arr(railsHit).map((rail) =>
        typeof rail === 'string' ? rail : str(bag(rail).explanation) || str(bag(rail).code) || 'safety rail applied',
      ),
      markdown: (() => {
        try {
          return coachEngine.renderReportMarkdown({ evaluation, proposal: safeProposal });
        } catch {
          return '';
        }
      })(),
      sessionsAnalyzed: num(metricsBag.sessionsAnalyzed) ?? arr(bag(packet).sessions).length,
      prCount: num(metricsBag.prCount) ?? arr(bag(packet).personalRecords).length,
      source: 'engine',
    };
  } catch {
    // Never let the coach engine take down the landing page.
    return {
      verdict: 'on_track',
      verdictLabel: 'On track',
      headline: 'Three weeks of clean progressive overload, then a fatigue signal in week four.',
      metrics: [
        { label: 'Volume this week', value: '—', hint: 'engine preview unavailable' },
        { label: 'Compliance', value: '—' },
        { label: 'Average RPE', value: '—' },
        { label: 'PRs in window', value: '10' },
      ],
      flags: [],
      changes: [],
      railsHit: [],
      markdown: '',
      sessionsAnalyzed: arr(bag(packet).sessions).length,
      prCount: arr(bag(packet).personalRecords).length,
      source: 'fallback',
    };
  }
}
